import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import { ProgressOverview } from '@/components/common/ProgressOverview';
import { RelatedRecordsSection } from '@/components/practitioner/shared/RelatedRecordsSection';
import {
  usePatientProgressComparisonQuery,
  usePatientProgressSeriesQuery,
} from '@/hooks/useProgressQueries';
import { RecordProgressModal } from './RecordProgressModal';

interface ProgressSectionProps {
  patientId: string;
}

export function ProgressSection({ patientId }: ProgressSectionProps) {
  const { t } = useTranslation('progress');
  const seriesQuery = usePatientProgressSeriesQuery(patientId);
  const comparisonQuery = usePatientProgressComparisonQuery(patientId);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const series = seriesQuery.data === undefined ? [] : seriesQuery.data;
  const comparisonEntries = comparisonQuery.data === undefined ? [] : comparisonQuery.data;

  function openRecordModal() {
    setIsModalOpen(true);
  }

  return (
    <>
      <RelatedRecordsSection
        title={t('section.title')}
        isLoading={seriesQuery.isLoading}
        isError={seriesQuery.isError}
        onRetry={function retry() {
          seriesQuery.refetch();
        }}
        actions={
          <Button variant="outline" size="chip" onClick={openRecordModal}>
            <Icon name="Plus" />
            {t('section.record')}
          </Button>
        }
      >
        {series.length === 0 ? (
          <div className="py-6 text-center">
            <Icon name="TrendingUp" className="mx-auto text-zoomuted text-2xl mb-2" />
            <p className="text-sm font-semibold text-zooindigo">{t('section.emptyTitle')}</p>
            <p className="text-xs text-zoomuted mt-1">{t('section.emptyDescription')}</p>
            <Button className="mt-3" size="chip" onClick={openRecordModal}>
              <Icon name="Plus" />
              {t('section.record')}
            </Button>
          </div>
        ) : (
          <ProgressOverview series={series} comparisonEntries={comparisonEntries} />
        )}
      </RelatedRecordsSection>

      {isModalOpen ? (
        <RecordProgressModal
          patientId={patientId}
          onClose={function closeModal() {
            setIsModalOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
